import React from 'react';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from '../../../../../../../../app/hooks';
import { setSelectedConversation, resetChatView } from '../../../../../../../../store/dashboard/askAica/actions';
import { ConversationListItem } from '../../../../../../../../store/dashboard/askAica/types';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../../../../utils/dateUtils';
import { getPlatformUserId } from '../../../../../../../../utils/utils';
import { leftPanelCloseIcon, rightPanelCloseIcon, AicaLogo, libraryIcon } from '../../../../../../../../assets/hostedassets';
import newChatIcon from '../../../../../../../../assets/border-color.svg';
import DeleteIcon from '../../../../../../../../assets/Svg/DeleteIcon';
import { Mixpanel } from '../../../../../../../../utils/mixpanel';
import {
    AICA_ASK_AICA_NEW_CHAT_CLICKED,
    AICA_ASK_AICA_DELETE_CHAT_CLICKED,
} from '../../../../../../../../utils/constants/mixpanelEvents/investorEvents';
import Button from '../../../../../../../../common/_custom/Button/Button';
import {
    ASK_AICA_LIBRARY_LABEL,
    ASK_AICA_SEE_ALL_LABEL,
    ASK_AICA_SEE_LESS_LABEL,
} from '../../../../../../../../store/dashboard/askAica/constants';
import { useCollapsibleTeamList } from './useCollapsibleTeamList';
import { useAskAicaSurface, ASK_AICA_SURFACE } from '../../context/AskAicaSurfaceContext';
import styles from './ConversationSidebar.module.scss';

interface ConversationSectionProps {
    title: string;
    items: ConversationListItem[];
    selectedConversationId: string | null;
    onRowClick: (id: string) => void;
    emptyHint: string;
    viewOnlyBadge?: boolean;
    loggedInUserName?: string;
    /** Delete affordance rendered ONLY for the caller's own rows — never on team rows. */
    onDeleteClick?: (id: string) => void;
    /** True while a delete round-trip is in flight — disables the row's resume
     * and delete buttons so a fast double-click can't fire two deletes (mirrors
     * LeftPanel's header Delete Chat button). */
    isClearingChat?: boolean;
    className?: string;
    rowsClassName?: string;
    footer?: React.ReactNode;
}

const ConversationSection: React.FC<ConversationSectionProps> = ({
    title,
    items,
    selectedConversationId,
    onRowClick,
    emptyHint,
    viewOnlyBadge,
    loggedInUserName,
    onDeleteClick,
    isClearingChat,
    className,
    rowsClassName,
    footer,
}) => {
    const rows = items.map((c: ConversationListItem) => (
        <ConversationRow
            key={c.conversation_id}
            conversationId={c.conversation_id}
            title={c.title}
            userName={c.user_name}
            lastMessageAt={c.last_message_at}
            isSelected={selectedConversationId === c.conversation_id}
            onClick={onRowClick}
            loggedInUserName={loggedInUserName}
            onDeleteClick={onDeleteClick}
            isClearingChat={isClearingChat}
        />
    ));
    return (
        <div className={clsx(styles.Section, className)}>
            <div className={styles.SectionHeaderRow}>
                <span className={styles.SectionHeader}>{title}</span>
                {viewOnlyBadge && <span className={styles.ViewOnlyBadge}>VIEW ONLY</span>}
            </div>
            {items.length === 0 ? (
                <span className={styles.EmptyHint}>{emptyHint}</span>
            ) : rowsClassName ? (
                <div className={rowsClassName}>{rows}</div>
            ) : (
                rows
            )}
            {footer}
        </div>
    );
};

// Extracted sub-component so the renderer is defined once and used in both
// .map() calls — avoids an in-body function re-created on every render.
interface ConversationRowProps {
    conversationId: string;
    title: string | null;
    userName: string | null;
    lastMessageAt: string | null;
    isSelected: boolean;
    onClick: (id: string) => void;
    /** When true, fall back to the logged-in user's display name before "Unknown user" */
    loggedInUserName?: string;
    onDeleteClick?: (id: string) => void;
    isClearingChat?: boolean;
}

const ConversationRow: React.FC<ConversationRowProps> = ({
    conversationId,
    title,
    userName,
    lastMessageAt,
    isSelected,
    onClick,
    loggedInUserName,
    onDeleteClick,
    isClearingChat,
}) => {
    const displayTitle = title || 'Untitled';
    // FIX 5: own rows use the Redux display name when the backend user_name is null
    const displayName = userName || loggedInUserName || 'Unknown user';
    const timestamp = formatDateByFormat(lastMessageAt ?? undefined, DATE_FORMATS.LIBRARY_FILE_TIMESTAMP);

    return (
        // Outer <div> (not <button>) — a delete <button> cannot nest inside a
        // resume <button> (invalid HTML, breaks a11y). .Row stays the class/hover
        // target so existing styling/selectors are unaffected (R-4).
        <div className={`${styles.Row} ${isSelected ? styles.RowSelected : ''}`}>
            <button className={styles.RowMain} onClick={() => onClick(conversationId)} disabled={isClearingChat}>
                <span className={styles.RowTitle}>{displayTitle}</span>
                <span className={styles.RowSubtitle}>
                    {displayName}
                    {timestamp && (
                        <>
                            <span className={styles.RowDot}>•</span>
                            {timestamp}
                        </>
                    )}
                </span>
            </button>
            {onDeleteClick && (
                <button
                    type="button"
                    className={styles.RowDelete}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDeleteClick(conversationId);
                    }}
                    aria-label="Delete conversation"
                    title="Delete conversation"
                    disabled={isClearingChat}
                >
                    <DeleteIcon className="" width="14" height="14" />
                </button>
            )}
        </div>
    );
};

interface ConversationSidebarProps {
    fetchChatHistory: (isInitialFetch?: boolean, conversationId?: string | null, reset?: boolean) => void;
    /** Owned by AskAicaV2 so the LeftPanel header can host the collapsed-state
        affordances (New Chat + expand) instead of a separate 3rem strip. */
    collapsed: boolean;
    onCollapsedChange: (collapsed: boolean) => void;
    /** On mobile this is a slide-over drawer, not a persistent column — picking
        New Chat or any conversation row should return focus to the chat by
        closing the drawer. Desktop's collapse state is user-driven only. */
    isMobile?: boolean;
    /** Mobile only: fired when the user picks a chat (row, New Chat, Back to my chat), not on the collapse button. */
    onNavigate?: () => void;
    /** Hides the per-row delete button; a hover-revealed button swallows the first tap on touch. */
    hideRowDelete?: boolean;
    /** Mobile company drawer: pin Team's Conversations to the bottom, 3 rows with See all / See less. */
    collapsibleTeamSection?: boolean;
    /** Mobile drawer: renders a Library row under New Chat that opens the generated-files page. */
    onOpenLibrary?: () => void;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
    fetchChatHistory,
    collapsed,
    onCollapsedChange,
    isMobile,
    onNavigate,
    hideRowDelete = false,
    collapsibleTeamSection = false,
    onOpenLibrary,
}) => {
    const dispatch = useAppDispatch();
    const { newChat, deleteConversation, isClearingChat } = useAskAicaSurface();
    // Same source as the global Topbar: the per-domain logo served by the backend,
    // falling back to the bundled AICA mark.
    const logoUrl = useAppSelector((state) => state.user.domainMetadata?.logoUrl);

    const currentUserName = useAppSelector((state) => state.user.userData.userName);
    const { conversations, isLoadingConversations, selectedConversationId } = useAppSelector((state) => state.dashboard.askAicaReducer);

    const platformUserId = getPlatformUserId();
    const mine = conversations.filter((c: ConversationListItem) => c.user_id === platformUserId);
    const team = conversations.filter((c: ConversationListItem) => c.user_id !== platformUserId);

    const teamList = useCollapsibleTeamList(team, collapsibleTeamSection);

    // True when the user is viewing a teammate's (view-only) conversation.
    const viewingTeamConversation =
        !!selectedConversationId && team.some((c: ConversationListItem) => c.conversation_id === selectedConversationId);

    const finishMobileNavigation = () => {
        onCollapsedChange(true);
        onNavigate?.();
    };

    const handleRowClick = (conversationId: string) => {
        // Reset chat view BEFORE switching — clears messages/pagination so
        // initMessageThread does a clean replace, not an interleave. Also
        // ensures fetchChatHistory uses page=1 (reset=true) not the old page.
        dispatch(resetChatView());
        dispatch(setSelectedConversation(conversationId));
        fetchChatHistory(false, conversationId, true);
        if (isMobile) finishMobileNavigation();
    };

    const handleBackToOwn = () => {
        dispatch(resetChatView());
        dispatch(setSelectedConversation(null));
        // Bare call re-runs the open-time resolution (latest own chat by last_message_at), so
        // Delete Chat is re-enabled with an explicit target instead of being left disabled.
        fetchChatHistory();
        if (isMobile) finishMobileNavigation();
    };

    const handleNewChat = () => {
        newChat?.();
        Mixpanel.track(AICA_ASK_AICA_NEW_CHAT_CLICKED, { surface: ASK_AICA_SURFACE.LEADS });
        if (isMobile) finishMobileNavigation();
    };

    const handleDeleteClick = (conversationId: string) => {
        Mixpanel.track(AICA_ASK_AICA_DELETE_CHAT_CLICKED, {
            surface: ASK_AICA_SURFACE.LEADS,
            scope: conversationId === selectedConversationId ? 'active' : 'background',
        });
        deleteConversation?.(conversationId);
    };

    if (collapsed) {
        return (
            <div className={styles.SidebarCollapsed}>
                <div className={styles.CollapsedTopRow}>
                    <button className={styles.CollapseBtn} onClick={() => onCollapsedChange(false)} title="Expand conversations">
                        <img src={rightPanelCloseIcon} alt="" width="18" height="18" />
                    </button>
                </div>
                <div className={styles.CollapsedBody}>
                    <button className={styles.NewChatBtnCollapsed} onClick={handleNewChat} title="New Chat" aria-label="New Chat">
                        <img className={styles.NewChatIcon} src={newChatIcon} alt="" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.Sidebar}>
            {/* Top row: "Back to my chat" (left, only while viewing a teammate's chat) + collapse (right). */}
            <div className={styles.TopRow}>
                <div className={styles.TopRowLeft}>
                    <img className={styles.SidebarLogo} src={logoUrl || AicaLogo} alt="AICA" />
                    {viewingTeamConversation && (
                        <button className={styles.BackToOwnBtn} onClick={handleBackToOwn}>
                            <span className={styles.BackToOwnIcon}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="12" fill="var(--primary-purple)" />
                                    <path
                                        d="M15.5 12H8M11 8.5L7.5 12L11 15.5"
                                        stroke="#ffffff"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                            Back to my chat
                        </button>
                    )}
                </div>
                <button className={styles.CollapseBtn} onClick={() => onCollapsedChange(true)} title="Collapse conversations">
                    <img src={leftPanelCloseIcon} alt="" width="18" height="18" />
                </button>
            </div>

            <button className={styles.NewChatBtn} onClick={handleNewChat}>
                <img className={styles.NewChatIcon} src={newChatIcon} alt="" />
                New Chat
            </button>

            {onOpenLibrary && (
                <Button
                    type="button"
                    text={
                        <span className={styles.LibraryRowLabel}>
                            <img className={styles.NewChatIcon} src={libraryIcon} alt="" />
                            {ASK_AICA_LIBRARY_LABEL}
                        </span>
                    }
                    onClick={onOpenLibrary}
                    className={clsx(styles.NewChatBtn, styles.LibraryRowBtn)}
                />
            )}

            {isLoadingConversations ? (
                <div className={styles.Shimmer}>
                    {[1, 2, 3].map((i) => (
                        <div key={i} className={styles.ShimmerRow} />
                    ))}
                </div>
            ) : (
                <>
                    <ConversationSection
                        title="Your Conversations"
                        items={mine}
                        selectedConversationId={selectedConversationId}
                        onRowClick={handleRowClick}
                        emptyHint="No conversations yet"
                        loggedInUserName={currentUserName}
                        onDeleteClick={hideRowDelete ? undefined : handleDeleteClick}
                        isClearingChat={isClearingChat}
                    />

                    <ConversationSection
                        title="Team's Conversations"
                        items={teamList.visibleItems}
                        selectedConversationId={selectedConversationId}
                        onRowClick={handleRowClick}
                        emptyHint="No team conversations"
                        viewOnlyBadge
                        className={collapsibleTeamSection ? styles.PinnedSection : undefined}
                        rowsClassName={collapsibleTeamSection ? styles.PinnedRows : undefined}
                        footer={
                            teamList.showToggle && (
                                <Button
                                    type="button"
                                    text={teamList.expanded ? ASK_AICA_SEE_LESS_LABEL : ASK_AICA_SEE_ALL_LABEL}
                                    onClick={teamList.toggle}
                                    className={styles.TeamToggleBtn}
                                    aria-expanded={teamList.expanded}
                                />
                            )
                        }
                    />
                </>
            )}
        </div>
    );
};

export default ConversationSidebar;
